import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Visibility
} from '@mui/icons-material';
import MaintenanceFilters from './MaintenanceFilters';
import { filterMaintenance, getPriorityColor } from '../utils/maintenanceUtils';
import { formatDateTimeWithTimezone, formatDateWithTimezone } from '../../../utils/timezoneUtils';
import { getMaintenanceWorkOrderTypeLabel as getTypeLabel } from '../../../constants/maintenanceWorkOrderTypes';

const formatUserName = (user) => {
  if (!user) return '—';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || '—';
};

const SORTABLE_COLUMNS = [
  { id: 'workOrder', label: 'Work Order #' },
  { id: 'aircraft', label: 'Aircraft' },
  { id: 'type', label: 'Type' },
  { id: 'title', label: 'Title' },
  { id: 'priority', label: 'Priority' },
  { id: 'scheduledDate', label: 'Scheduled Date' },
  { id: 'completedDate', label: 'Completed Date' }
];

function getComparator(order, orderBy) {
  return (a, b) => {
    const getValue = (item, col) => {
      switch (col) {
        case 'workOrder':
          return (item.workOrderNumber || (item._id && item._id.toString().slice(-6))) || '';
        case 'aircraft':
          return (item.aircraftId?.registration || 'Unknown').toLowerCase();
        case 'type':
          return (item.type || '').toLowerCase();
        case 'title':
          return (item.title || '').toLowerCase();
        case 'priority':
          return (item.priority || '').toLowerCase();
        case 'scheduledDate':
          return item.scheduledDate ? new Date(item.scheduledDate).getTime() : 0;
        case 'completedDate':
          return item.completedDate ? new Date(item.completedDate).getTime() : 0;
        default:
          return '';
      }
    };
    const aVal = getValue(a, orderBy);
    const bVal = getValue(b, orderBy);
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  };
}

const formatWhen = (iso, tz, dateOnly = false) => {
  if (!iso) return '—';
  if (!tz) return new Date(iso).toLocaleString();
  return dateOnly
    ? formatDateWithTimezone(iso, tz)
    : formatDateTimeWithTimezone(iso, null, tz);
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

/**
 * Tab listing completed work orders with expandable completion details.
 */
const WorkOrderHistoryTab = ({
  completedWorkOrders,
  loadingCompleted,
  aircraft,
  displayTimezone,
  searchTerm,
  setSearchTerm,
  priorityFilter,
  setPriorityFilter,
  typeFilter,
  setTypeFilter,
  aircraftFilter,
  setAircraftFilter,
  onViewDetails
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('completedDate');
  const [order, setOrder] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  const filteredWorkOrders = useMemo(
    () => filterMaintenance(
      completedWorkOrders,
      searchTerm,
      'COMPLETED',
      priorityFilter,
      typeFilter,
      aircraftFilter
    ),
    [completedWorkOrders, searchTerm, priorityFilter, typeFilter, aircraftFilter]
  );

  const sortedWorkOrders = useMemo(
    () => [...filteredWorkOrders].sort(getComparator(order, orderBy)),
    [filteredWorkOrders, order, orderBy]
  );

  const paginatedWorkOrders = sortedWorkOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    setPage(0);
  }, [filteredWorkOrders.length, searchTerm, priorityFilter, typeFilter, aircraftFilter]);

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (loadingCompleted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Completed work orders are listed here with completion dates, MRO sign-off, CAMO QM acknowledgment,
        and maintenance release details. Use View or expand a row for full information.
      </Typography>

      <MaintenanceFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter="COMPLETED"
        setStatusFilter={() => {}}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        aircraftFilter={aircraftFilter}
        setAircraftFilter={setAircraftFilter}
        aircraft={aircraft}
        maintenance={completedWorkOrders}
        filteredMaintenance={filteredWorkOrders}
        hideStatusFilter
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {SORTABLE_COLUMNS.map((col) => (
                <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={(e) => handleRequestSort(e, col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Release Cert.</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWorkOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm || priorityFilter !== 'ALL' || typeFilter !== 'ALL' || aircraftFilter !== 'ALL'
                      ? 'No completed work orders match the current filters'
                      : 'No completed work orders found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedWorkOrders.map((item) => {
                const rowId = item._id;
                const isExpanded = expandedId === rowId;
                const hasRelease = Boolean(item.maintenanceRelease?.releasedAt || item.maintenanceRelease?.releaseCertificate);
                const hasCrs = Boolean(item.crsDocument?.originalName);

                return (
                  <React.Fragment key={rowId}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => toggleExpanded(rowId)} aria-label="Expand details">
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          WO #{item.workOrderNumber || (item._id && item._id.toString().slice(-6)) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.aircraftId?.registration || 'Unknown'}</TableCell>
                      <TableCell>{getTypeLabel(item.type)}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>
                        <Chip label={item.priority} color={getPriorityColor(item.priority)} size="small" />
                      </TableCell>
                      <TableCell>
                        {item.scheduledDate
                          ? formatWhen(item.scheduledDate, displayTimezone, true)
                          : 'Not scheduled'}
                      </TableCell>
                      <TableCell>
                        {formatWhen(item.completedDate, displayTimezone)}
                      </TableCell>
                      <TableCell>
                        {hasRelease ? (
                          <Chip label="Issued" color="success" size="small" />
                        ) : hasCrs ? (
                          <Chip label="CRS" color="info" size="small" />
                        ) : (
                          <Chip label="None" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View full details">
                          <IconButton size="small" onClick={() => onViewDetails(item)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Completion details
                            </Typography>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                                gap: 2
                              }}
                            >
                              <DetailRow label="Description" value={item.description} />
                              <DetailRow
                                label="Created by"
                                value={`${formatUserName(item.createdBy)}${item.createdAt ? ` • ${formatWhen(item.createdAt, displayTimezone)}` : ''}`}
                              />
                              <DetailRow
                                label="MRO engineer completion"
                                value={
                                  item.mroEngineerCompleted
                                    ? `${formatUserName(item.mroEngineerCompletedBy)} • ${formatWhen(item.mroEngineerCompletedAt, displayTimezone)}${item.mroEngineerNotes ? ` — ${item.mroEngineerNotes}` : ''}`
                                    : '—'
                                }
                              />
                              <DetailRow
                                label="MRO QM completion acknowledgment"
                                value={
                                  item.mroQmCompletionAcknowledged
                                    ? `${formatUserName(item.mroQmCompletionAcknowledgedBy)} • ${formatWhen(item.mroQmCompletionAcknowledgedAt, displayTimezone)}`
                                    : '—'
                                }
                              />
                              <DetailRow
                                label="CAMO QM completion acknowledgment"
                                value={
                                  item.camoQmCompletionAcknowledged
                                    ? `${formatUserName(item.camoQmCompletionAcknowledgedBy)} • ${formatWhen(item.camoQmCompletionAcknowledgedAt, displayTimezone)}`
                                    : '—'
                                }
                              />
                              <DetailRow
                                label="Maintenance release"
                                value={
                                  hasRelease
                                    ? `${formatUserName(item.maintenanceRelease?.releasedBy)} • ${formatWhen(item.maintenanceRelease?.releasedAt, displayTimezone)}${item.maintenanceRelease?.certificateType ? ` (${item.maintenanceRelease.certificateType.replace(/_/g, ' ')})` : ''}`
                                    : '—'
                                }
                              />
                              <DetailRow
                                label="CRS document"
                                value={hasCrs ? item.crsDocument.originalName : '—'}
                              />
                              {item.creationSourceRemarks && (
                                <DetailRow label="Creation source / reason" value={item.creationSourceRemarks} />
                              )}
                              {item.standaloneEmergencyRepair && (
                                <DetailRow label="Type note" value="Stand-alone emergency work order" />
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {filteredWorkOrders.length > 0 && (
          <TablePagination
            component="div"
            count={filteredWorkOrders.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </TableContainer>
    </>
  );
};

export default WorkOrderHistoryTab;
